import { z } from 'zod';
import { CrewClaimResolutionMethodEnum, CrewClaimRollupStatusCode, ReportColumn, } from '../../enums/crew.js';
import { ClaimReasonEnum, ClaimResolutionMethodEnum } from '../../enums/gsp.js';
import { TrackingStatusCode } from '../../enums/tracking.js';
import { enumToZodLiteralUnion, helperSchema } from '../../zodExtensions.js';
const arrayWithMinOne = (schema) => z.array(schema).min(1);
const nullableDateString = z.string().datetime().nullable();
const dateRange = z.object({
    start: nullableDateString,
    end: nullableDateString,
});
const baseQuery = z.object({
    searchTerm: helperSchema.optionalizedString,
    createdOn: dateRange.optional(),
});
const registrationQuery = z.object({
    kind: z.literal('Registration'),
    filters: z
        .object({
        salesChannels: arrayWithMinOne(z.string()).optional(),
        estimatedPurchaseDate: dateRange.optional(),
        hasClaim: z.boolean().optional(),
        hasBeenCanceled: z.boolean().optional(),
    })
        .merge(baseQuery),
});
const returnOrWarranty = z.object({
    hasError: z.boolean().optional(),
    hasInspection: z.boolean().optional(),
    hasShipment: z.boolean().optional(),
    source: arrayWithMinOne(z.union([z.literal('Merchant_App'), z.literal('Customer_App')])).optional(),
    shipmentStatus: arrayWithMinOne(enumToZodLiteralUnion(TrackingStatusCode)).optional(),
    statusCodes: arrayWithMinOne(enumToZodLiteralUnion(CrewClaimRollupStatusCode)).optional(),
    claimTaggedWith: arrayWithMinOne(z.string()).optional(),
    requestedResolutionMethods: arrayWithMinOne(enumToZodLiteralUnion(CrewClaimResolutionMethodEnum)).optional(),
});
// ----- return or warranty query -----
const returnQuery = z.object({
    kind: z.literal('Return'),
    filters: returnOrWarranty.merge(baseQuery).extend({
        hasInstantExchange: z.boolean().optional(),
    }),
});
const warrantyQuery = z.object({
    kind: z.literal('Warranty'),
    filters: returnOrWarranty.merge(baseQuery).extend({
        salesChannels: arrayWithMinOne(z.string()).optional(),
        hasRegistration: z.boolean().optional(),
    }),
});
const shippingClaimListQuery = z.object({
    kind: z.literal('Shipping'),
    filters: z
        .object({
        resolutionMethods: arrayWithMinOne(enumToZodLiteralUnion(ClaimResolutionMethodEnum)).optional(),
        claimReasons: arrayWithMinOne(enumToZodLiteralUnion(ClaimReasonEnum)).optional(),
    })
        .merge(baseQuery),
});
const orderListQuery = z.object({
    kind: z.literal('Order'),
    filters: baseQuery,
});
const baseSearchReq = z.object({
    cursor: z
        .object({
        id: z.number(),
        direction: z.union([z.literal('next'), z.literal('prev')]),
    })
        .nullable(),
    orderBy: z.union([z.literal('asc'), z.literal('desc')]),
    take: z.number().default(50),
});
export const searchFilterReq = z.discriminatedUnion('kind', [
    shippingClaimListQuery.merge(baseSearchReq),
    returnQuery.merge(baseSearchReq),
    warrantyQuery.merge(baseSearchReq),
    registrationQuery.merge(baseSearchReq),
    orderListQuery.merge(baseSearchReq),
]);
// ------------ views ------------
var RollingDatePreset;
(function (RollingDatePreset) {
    RollingDatePreset["today"] = "0d";
    RollingDatePreset["last7Days"] = "7d";
    RollingDatePreset["last30Days"] = "30d";
    RollingDatePreset["last90Days"] = "90d";
    RollingDatePreset["last12Months"] = "12m";
})(RollingDatePreset || (RollingDatePreset = {}));
export const viewDateValue = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('preset'),
        preset: enumToZodLiteralUnion(RollingDatePreset),
    }),
    z.object({
        kind: z.literal('custom'),
        range: dateRange,
    }),
]);
const baseViewFilters = baseQuery.extend({
    createdOn: viewDateValue.optional(),
});
const returnViewFilters = returnQuery.extend({
    filters: returnQuery.shape.filters.merge(baseViewFilters),
});
const warrantyViewFilters = warrantyQuery.extend({
    filters: warrantyQuery.shape.filters.merge(baseViewFilters),
});
const registrationViewFilters = registrationQuery.extend({
    filters: registrationQuery.shape.filters
        .extend({
        estimatedPurchaseDate: viewDateValue.optional(),
    })
        .merge(baseViewFilters),
});
const shippingClaimListViewFilters = shippingClaimListQuery.extend({
    filters: shippingClaimListQuery.shape.filters.merge(baseViewFilters),
});
const orderListViewFilters = orderListQuery.extend({
    filters: orderListQuery.shape.filters.merge(baseViewFilters),
});
const baseView = z.object({
    id: z.number(),
    name: helperSchema.nonEmptyString.max(40),
});
export const viewFilters = z.union([
    returnViewFilters.shape.filters,
    warrantyViewFilters.shape.filters,
    registrationViewFilters.shape.filters,
    shippingClaimListViewFilters.shape.filters,
    orderListViewFilters.shape.filters,
]);
export const storeUiView = z.discriminatedUnion('kind', [
    returnViewFilters.merge(baseView),
    warrantyViewFilters.merge(baseView),
    registrationViewFilters.merge(baseView),
    shippingClaimListViewFilters.merge(baseView),
    orderListViewFilters.merge(baseView),
]);
export const storeUiViewCreate = z.discriminatedUnion('kind', [
    returnViewFilters.merge(baseView.omit({ id: true })),
    warrantyViewFilters.merge(baseView.omit({ id: true })),
    registrationViewFilters.merge(baseView.omit({ id: true })),
    shippingClaimListViewFilters.merge(baseView.omit({ id: true })),
    orderListViewFilters.merge(baseView.omit({ id: true })),
]);
// ------------ responses ------------
export const pageInfo = z.object({
    nextCursor: z.number().nullable(),
    prevCursor: z.number().nullable(),
});
export const reportRequestFormValues = z.object({
    columns: z.array(enumToZodLiteralUnion(ReportColumn)),
    lineItemCustomFieldIds: z.array(z.number()),
    inspectionCustomFieldIds: z.array(z.number()),
});
const baseReportRequest = baseSearchReq.merge(reportRequestFormValues).extend({
    email: z.string(),
});
export const reportRequestBody = z.discriminatedUnion('kind', [
    returnQuery.merge(baseReportRequest),
    warrantyQuery.merge(baseReportRequest),
]);
//# sourceMappingURL=filters.js.map